import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowDownToLine, Equal, Check, X, Loader2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  keep_at_l2: {
    label: 'Keep at L2',
    icon: Equal,
    color: 'bg-blue-600 hover:bg-blue-700',
    description: 'Clear L3 on records where this value sits deep; flag cleared on L2 rows. Value only exists at L2 after this.',
  },
  promote_to_l3: {
    label: 'Promote to L3',
    icon: ArrowDownToLine,
    color: 'bg-emerald-600 hover:bg-emerald-700',
    description: 'Flagged L2 rows get re-parented under your chosen L2, and the value moves to L3. Value only exists at L3 after this.',
  },
  dismiss: {
    label: 'Dismiss',
    icon: X,
    color: 'bg-slate-500 hover:bg-slate-600',
    description: 'Clear needs_review flag only. Data stays as-is — pick this when the collision is intentional.',
  },
};

const CollisionCard = ({ collision, token, uploadId, onResolved }) => {
  const [action, setAction] = useState(collision.suggested_action);
  const [parentL2, setParentL2] = useState(collision.suggested_parent_l2 || '');
  const [customParent, setCustomParent] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const effectiveParentL2 = parentL2 === '__custom__' ? customParent.trim() : parentL2;

  const handleApply = async () => {
    if (action === 'promote_to_l3' && !effectiveParentL2) {
      toast.error('Pick a parent L2 first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/data-refinery/hierarchy-collisions/${uploadId}/resolve`,
        { value: collision.value, action, parent_l2: effectiveParentL2 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const msg = action === 'keep_at_l2'
        ? `Kept "${collision.value}" at L2 — ${res.data.l2_records_affected} flag cleared, ${res.data.l3_records_affected} L3 cleared.`
        : action === 'promote_to_l3'
        ? `Promoted "${collision.value}" to L3 under "${effectiveParentL2}" — ${res.data.l2_records_affected} records updated.`
        : `Dismissed "${collision.value}" — ${res.data.l2_records_affected} flags cleared.`;
      toast.success(msg);
      onResolved(collision.value);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resolution failed');
    } finally {
      setSubmitting(false);
    }
  };

  const ActionIcon = ACTION_META[action].icon;
  const totalSpend = (collision.l2_spend || 0) + (collision.l3_spend || 0);
  const l2Pct = totalSpend > 0 ? ((collision.l2_spend / totalSpend) * 100).toFixed(0) : 0;
  const l3Pct = totalSpend > 0 ? ((collision.l3_spend / totalSpend) * 100).toFixed(0) : 0;

  return (
    <div className="border border-rose-200 bg-white rounded-sm overflow-hidden"
         data-testid={`collision-card-${collision.value}`}>
      <div className="p-4 border-b border-rose-100 bg-rose-50/40">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={16} className="text-rose-600 flex-shrink-0" />
              <h3 className="text-base font-heading font-semibold text-slate-900 truncate">
                "{collision.value}"
              </h3>
              <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full flex-shrink-0">
                Collision
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
              <span>
                <span className="font-semibold text-slate-900">{collision.l2_flagged}</span> flagged at L2
                {' '}<span className="text-slate-400">({l2Pct}% spend)</span>
              </span>
              <span className="text-slate-300">·</span>
              <span>
                <span className="font-semibold text-slate-900">{collision.l3_count}</span> at L3
                {' '}<span className="text-slate-400">({l3Pct}% spend)</span>
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">
                Total spend: {totalSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 flex-shrink-0"
            data-testid={`toggle-collision-${collision.value}`}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? 'Hide' : 'Preview'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-b border-slate-100 bg-slate-50/40 space-y-3">
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1.5">
              At L2 (flagged) — sample of {collision.sample_l2.length}
            </p>
            <div className="space-y-1 text-xs">
              {collision.sample_l2.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-slate-600">
                  <span className="truncate flex-1">{r.supplier} · {r.description}</span>
                  <span className="text-slate-400 font-mono">
                    {r.category_l1} › <span className="text-rose-600 font-semibold">{r.category_l2}</span> › {r.category_l3 || '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1.5">
              At L3 — sample of {collision.sample_l3.length}
            </p>
            <div className="space-y-1 text-xs">
              {collision.sample_l3.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-slate-600">
                  <span className="truncate flex-1">{r.supplier} · {r.description}</span>
                  <span className="text-slate-400 font-mono">
                    {r.category_l1} › {r.category_l2} › <span className="text-emerald-600 font-semibold">{r.category_l3}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-sm p-0.5 w-fit">
          {Object.entries(ACTION_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const isSuggested = collision.suggested_action === key;
            return (
              <button
                key={key}
                onClick={() => setAction(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all flex items-center gap-1.5 ${
                  action === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`action-${key}-${collision.value}`}
              >
                <Icon size={12} />
                {meta.label}
                {isSuggested && (
                  <span className="ml-0.5 flex items-center" title="Suggested by spend weight">
                    <Sparkles size={10} className="text-amber-500" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 italic">{ACTION_META[action].description}</p>

        {action === 'promote_to_l3' && (
          <div className="flex items-center gap-2" data-testid={`parent-l2-picker-${collision.value}`}>
            <label className="text-xs font-semibold text-slate-600 flex-shrink-0">Parent L2:</label>
            <select
              value={parentL2}
              onChange={(e) => setParentL2(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-slate-200 rounded-sm text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`parent-l2-select-${collision.value}`}
            >
              {collision.parent_candidates.map((c) => (
                <option key={c} value={c}>{c} {c === collision.suggested_parent_l2 ? '(suggested)' : ''}</option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
            {parentL2 === '__custom__' && (
              <input
                value={customParent}
                onChange={(e) => setCustomParent(e.target.value)}
                placeholder="Type custom L2"
                className="flex-1 px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid={`parent-l2-custom-${collision.value}`}
              />
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleApply}
            disabled={submitting}
            className={`${ACTION_META[action].color} text-white gap-1.5`}
            data-testid={`apply-collision-${collision.value}`}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ActionIcon size={14} />}
            {submitting ? 'Applying…' : <>Apply <Check size={12} /></>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const HierarchyCollisionResolver = ({ token, uploadId, onResolved }) => {
  const [collisions, setCollisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const [resolvedIds, setResolvedIds] = useState(new Set());

  const fetchCollisions = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/data-refinery/hierarchy-collisions/${uploadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCollisions(res.data.collisions || []);
      setResolvedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load collisions');
    } finally {
      setLoading(false);
    }
  }, [uploadId, token]);

  useEffect(() => { fetchCollisions(); }, [fetchCollisions]);

  const handleResolvedLocal = (value) => {
    setResolvedIds((prev) => new Set([...prev, value]));
    if (onResolved) onResolved();
  };

  const handleBulkDismiss = async () => {
    if (!window.confirm(
      `Dismiss all ${pendingCollisions.length} collision flags?\n\n` +
      'This keeps all data as-is and just clears the needs_review tags. ' +
      'Use this when you\'ve reviewed the list and accept the collisions are intentional.'
    )) return;
    setBulkDismissing(true);
    try {
      const res = await axios.post(
        `${API}/data-refinery/hierarchy-collisions/${uploadId}/bulk-dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cleared ${res.data.records_affected.toLocaleString()} flags`);
      setResolvedIds(new Set(collisions.map((c) => c.value)));
      if (onResolved) onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk dismiss failed');
    } finally {
      setBulkDismissing(false);
    }
  };

  const pendingCollisions = collisions.filter((c) => !resolvedIds.has(c.value));

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 bg-slate-50 border border-slate-200 rounded-sm"
           data-testid="collision-resolver-loading">
        <Loader2 size={16} className="animate-spin text-slate-400" />
        <span className="text-sm text-slate-600">Loading hierarchy collisions…</span>
      </div>
    );
  }

  if (collisions.length === 0) {
    return (
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm text-sm text-emerald-700"
           data-testid="collision-resolver-empty">
        No L2↔L3 hierarchy collisions detected. Taxonomy is clean.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="hierarchy-collision-resolver">
      <div className="flex items-start justify-between gap-4 p-4 bg-rose-50 border border-rose-200 rounded-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-rose-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-rose-800">
              {pendingCollisions.length} of {collisions.length} hierarchy collisions pending resolution
            </p>
            <p className="text-xs text-rose-700 mt-1">
              Same label used at both L2 and L3. Choose where each one belongs to lift your Trust Score.
              {' '}<Sparkles size={10} className="inline text-amber-500 mb-0.5" /> marks suggested actions by spend weight.
            </p>
          </div>
        </div>
        {pendingCollisions.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkDismiss}
            disabled={bulkDismissing}
            className="flex-shrink-0 border-rose-400 text-rose-700 hover:bg-rose-100 gap-1.5"
            data-testid="bulk-dismiss-collisions-button"
          >
            {bulkDismissing ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
            Dismiss all
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {pendingCollisions.map((c) => (
          <CollisionCard
            key={c.value}
            collision={c}
            token={token}
            uploadId={uploadId}
            onResolved={handleResolvedLocal}
          />
        ))}
      </div>

      {resolvedIds.size > 0 && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm text-xs text-emerald-700"
             data-testid="collisions-resolved-count">
          <Check size={12} className="inline mr-1" />
          Resolved {resolvedIds.size} collision{resolvedIds.size !== 1 ? 's' : ''} in this session
        </div>
      )}
    </div>
  );
};
